"use client"

import { useRef, useState } from "react"
// Google recommends PlaceAutocompleteElement for new use; Autocomplete is still supported with bug fixes.
// Migration: https://developers.google.com/maps/documentation/javascript/places-migration-overview
import { Autocomplete } from "@react-google-maps/api"
import { Controller, useFormContext, Control, FieldValues, ControllerRenderProps, useWatch } from "react-hook-form"
import { Check, ChevronsUpDown } from "lucide-react"
import { Input } from "../ui/new-input"
import FormTextInputWrapper from "./form-input-wrapper"
import { MapPinDrop } from "./map-pin-drop"
import { Button } from "../ui/button"
import { Popover, PopoverAnchor, PopoverContent } from "../ui/popover"
import { cn } from "@/shared/lib/utils"
import {
  getDisplayAddressValue,
  isUnknownLocationValue,
  LOCATION_CURRENTLY_UNKNOWN_OPTION,
} from "@/features/homeowners/lib/unknown-location"
import UnknownLocationInfoIcon from "@/shared/components/unknown-location-info-icon"

type LocationDetails = {
  city: string
  state: string
  country: string
  latitude?: number
  longitude?: number
  zipCode?: string
}

interface StateCountryAutocompleteProps {
  setLocationDetails: (location: LocationDetails | null) => void
  locationDetails: LocationDetails | null
  control: Control<any>
  id?: any
  /** When true, show yellow background when value is empty (e.g. required on Profile) */
  highlightWhenEmpty?: boolean
  /** Optional label override (e.g. "Address (including city, state and zip)" without asterisk when not required) */
  label?: string
  /** Optional field name override (e.g. "volunteers.0.preferredLocation" for nested forms) */
  fieldName?: string
  /** Admin/Base Leader: show Location Currently Unknown dropdown (homeowner create/edit). */
  allowUnknownLocation?: boolean
}

function toLatLngLiteral(
  loc: google.maps.LatLng | google.maps.LatLngLiteral
): google.maps.LatLngLiteral {
  if (typeof (loc as google.maps.LatLng).lat === "function") {
    const ll = loc as google.maps.LatLng
    return { lat: ll.lat(), lng: ll.lng() }
  }
  return loc as google.maps.LatLngLiteral
}

/** For nested volunteer address fields (e.g. volunteers.0.preferredLocation). */
function nestedPreferredLocationCoordPaths(fieldNameOverride: string | undefined): {
  latitude: string
  longitude: string
  prefilled: string
} | null {
  if (!fieldNameOverride?.endsWith(".preferredLocation")) return null
  const prefix = fieldNameOverride.slice(0, -".preferredLocation".length)
  return {
    latitude: `${prefix}.preferredLocationLatitude`,
    longitude: `${prefix}.preferredLocationLongitude`,
    prefilled: `${prefix}.preferredLocationPrefilled`,
  }
}

export const StateCountryAutocomplete: React.FC<
  StateCountryAutocompleteProps
> = ({ setLocationDetails, locationDetails, control, id, highlightWhenEmpty, label: labelProp, fieldName: fieldNameOverride, allowUnknownLocation = false }) => {
  const { setValue, clearErrors, getValues } = useFormContext()
  const [error, setError] = useState<string>("")
  const [showMapPinDrop, setShowMapPinDrop] = useState(false)
  const [isUnknownLocationDropdownOpen, setIsUnknownLocationDropdownOpen] = useState(false)
  const acRef = useRef<google.maps.places.Autocomplete | null>(null)
  /** Skip clearing Google validation when Places fills the input after a selection. */
  const skipClearValidationRef = useRef(false)
  const showUnknownLocationOption = allowUnknownLocation && !fieldNameOverride && !(id === 1 || id === "1")

  // Handle both string "1" and number 1 for id comparison
  const isProfileForm = id === 1 || id === "1"
  const controllerName = fieldNameOverride ?? (isProfileForm ? "preferredLocation" : "home.address")
  const nestedCoordPaths = nestedPreferredLocationCoordPaths(fieldNameOverride)

  const setNestedValidatedCoords = (lat?: number, lng?: number) => {
    if (!nestedCoordPaths) return
    setValue(nestedCoordPaths.latitude as any, lat, { shouldDirty: true })
    setValue(nestedCoordPaths.longitude as any, lng, { shouldDirty: true })
    setValue(nestedCoordPaths.prefilled as any, false, { shouldDirty: true })
  }

  const clearNestedValidatedCoords = () => {
    if (!nestedCoordPaths) return
    setValue(nestedCoordPaths.latitude as any, undefined, { shouldDirty: true })
    setValue(nestedCoordPaths.longitude as any, undefined, { shouldDirty: true })
    setValue(nestedCoordPaths.prefilled as any, false, { shouldDirty: true })
  }

  const handleLoad = (autocomplete: google.maps.places.Autocomplete) => {
    acRef.current = autocomplete
  }

  const handlePlaceChanged = (onChange: (val: string) => void) => {
    if (!acRef.current) return
    const place = acRef.current.getPlace()
    if (!place.formatted_address) return
    skipClearValidationRef.current = true
    onChange(place.formatted_address)

    const components = place.address_components
    if (!place || !components || !place.geometry?.location) return

    let city = ""
    let stateShortName = ""
    let countryShortName = ""
    let zipCode = ""

    components.forEach((component) => {
      const types = component.types
      // Try multiple city types - some addresses use different types
      if (!city) {
        if (types.includes("locality")) {
          city = component.long_name
        } else if (types.includes("sublocality")) {
          city = component.long_name
        } else if (types.includes("sublocality_level_1")) {
          city = component.long_name
        } else if (types.includes("neighborhood")) {
          city = component.long_name
        } else if (types.includes("postal_town")) {
          city = component.long_name
        }
      }
      if (types.includes("administrative_area_level_1"))
        stateShortName = component.long_name
      if (types.includes("country")) countryShortName = component.long_name
      if (types.includes("postal_code")) zipCode = component.long_name
    })

    const { lat, lng } = toLatLngLiteral(place.geometry.location)

    // If city is still empty, try to extract from formatted_address
    if (!city && place.formatted_address) {
      // Try to extract city from formatted address (usually between street and state)
      const addressParts = place.formatted_address.split(",").map(s => s.trim())
      // City is typically the second-to-last part before state
      if (addressParts.length >= 2) {
        city = addressParts[addressParts.length - 2]
      }
    }

    // State and country are required - city should be set but we'll use a fallback if needed
    if (!stateShortName || !countryShortName) {
      setError("Please select a valid address that includes both state and country.")
      setLocationDetails(null)
      clearNestedValidatedCoords()
      // Clear form values
      setValue("home.city", "", { shouldDirty: true })
      setValue("home.state", "", { shouldDirty: true })
      setValue("home.country", "", { shouldDirty: true })
      setValue("home.zipCode", "", { shouldDirty: true })
      return
    }

    // If city is still empty after all attempts, use a default or extract from address
    if (!city) {
      // Use the first part of the formatted address as a fallback
      if (place.formatted_address) {
        const parts = place.formatted_address.split(",")
        city = parts[0]?.trim() || "Unknown"
      } else {
        city = "Unknown"
      }
    }

    setError("")
    clearErrors(controllerName as any)
    const newLocation: LocationDetails = {
      city,
      state: stateShortName,
      country: countryShortName,
      latitude: lat,
      longitude: lng,
      zipCode,
    }
    setLocationDetails(newLocation)
    setValue(controllerName, place.formatted_address, { shouldDirty: true })
    setNestedValidatedCoords(lat, lng)
    if (!fieldNameOverride) {
      setValue("home.city", city, { shouldDirty: true })
      setValue("home.state", stateShortName, { shouldDirty: true })
      setValue("home.country", countryShortName, { shouldDirty: true })
      setValue("home.zipCode", zipCode, { shouldDirty: true })
      setValue("home.latitude", lat, { shouldDirty: true })
      setValue("home.longitude", lng, { shouldDirty: true })
    }
  }

  const handleMapLocationSelected = (location: LocationDetails) => {
    // MapPinDrop has already set city/state/country/zip/lat/lng and home.address (user description).
    // This runs only after the user presses "Use This Location" in the map dialog.
    setLocationDetails(location)
    setShowMapPinDrop(false)
    setError("")
    clearErrors(controllerName as any)
    setNestedValidatedCoords(location.latitude, location.longitude)
    if (isProfileForm) {
      const description = getValues("home.address")
      const trimmed =
        typeof description === "string" ? description.trim() : String(description ?? "").trim()
      if (trimmed) {
        setValue(controllerName, trimmed, { shouldDirty: true })
      } else {
        const fallback = [location.city, location.state, location.country]
          .filter(Boolean)
          .join(", ")
        if (fallback) {
          setValue(controllerName, fallback, { shouldDirty: true })
        }
      }
    }
  }

  const clearHomeLocationFields = () => {
    setValue("home.city", "", { shouldDirty: true })
    setValue("home.state", "", { shouldDirty: true })
    setValue("home.country", "", { shouldDirty: true })
    setValue("home.zipCode", "", { shouldDirty: true })
    setValue("home.latitude", null, { shouldDirty: true })
    setValue("home.longitude", null, { shouldDirty: true })
    setLocationDetails(null)
    clearNestedValidatedCoords()
  }

  const selectUnknownLocation = (onChange: (val: string) => void) => {
    skipClearValidationRef.current = true
    onChange(LOCATION_CURRENTLY_UNKNOWN_OPTION)
    setValue(controllerName, LOCATION_CURRENTLY_UNKNOWN_OPTION, { shouldDirty: true, shouldValidate: true })
    clearHomeLocationFields()
    setError("")
    clearErrors(controllerName as any)
    setIsUnknownLocationDropdownOpen(false)
  }

  const openMapPinDrop = () => {
    clearErrors(controllerName as any)
    setError("")
    setShowMapPinDrop(true)
  }
  // Always call useWatch unconditionally (React Hook rules)
  const watchedPreferredLocation = useWatch({ control, name: controllerName })
  const watchedValue = (isProfileForm || fieldNameOverride) ? watchedPreferredLocation : undefined

  // Show map pin drop if enabled
  if (showMapPinDrop) {
    return (
      <div className="w-full">
        <MapPinDrop
          onLocationSelected={handleMapLocationSelected}
          onClose={() => setShowMapPinDrop(false)}
          control={control}
        />
      </div>
    )
  }

  return (
    <div className="w-full min-w-0">
      <Controller
        name={controllerName}
        control={control}
        render={({ field, fieldState }: { field: ControllerRenderProps<FieldValues, any>; fieldState: { error?: { message?: string } } }) => {
          // Use watched value if field value is empty but watched value exists
          const rawValue = field.value || watchedValue || ""
          const displayValue = getDisplayAddressValue(String(rawValue ?? ""))
          const validationError = fieldState?.error?.message
          const hasError = !!validationError || !!error
          const showEmptyHighlight = highlightWhenEmpty && !displayValue?.trim()
          const defaultLabel = (
            <span className="inline-flex items-center gap-1">
              Address (including city, state and zip) <span className="whitespace-nowrap">*</span>
              {showUnknownLocationOption && <UnknownLocationInfoIcon />}
            </span>
          )
          const addressInput = (
            <Input
              placeholder="Address (including city, state and zip)"
              value={displayValue}
              variant={hasError ? "destructive" : "default"}
              className={cn(
                showUnknownLocationOption && "pr-8",
                hasError ? "border-destructive" : showEmptyHighlight ? "!border-[var(--color-pizza-600)] focus:!border-[var(--color-pizza-600)] focus:!ring-[var(--color-pizza-600)]/20" : undefined
              )}
              style={showEmptyHighlight ? { backgroundColor: "var(--color-pizza-600)", opacity: 0.3, color: "#000000" } : undefined}
              onChange={(e) => {
                field.onChange(e.target.value)
                // Free-typing invalidates a prior Google/map selection (unless Places just filled the input)
                if (skipClearValidationRef.current) {
                  skipClearValidationRef.current = false
                } else {
                  setLocationDetails(null)
                  clearNestedValidatedCoords()
                  setError("")
                }
                // If address is cleared, also clear city and state
                if (!e.target.value || e.target.value.trim() === "") {
                  clearHomeLocationFields()
                }
              }}
              onFocus={() => {
                if (showUnknownLocationOption) setIsUnknownLocationDropdownOpen(true)
              }}
              onClick={() => {
                if (showUnknownLocationOption) setIsUnknownLocationDropdownOpen(true)
              }}
            />
          )
          
          return (
            <Autocomplete
              onLoad={handleLoad}
              onPlaceChanged={() => handlePlaceChanged(field.onChange)}
              options={{ types: ["address"] }}
              className="block w-full"
            >
              <FormTextInputWrapper
                inputGroupProps={{
                  label: labelProp ?? defaultLabel,
                  error: validationError || error || undefined,
                  wrapperClassName: "w-full",
                }}
              >
                {showUnknownLocationOption ? (
                  <Popover open={isUnknownLocationDropdownOpen} onOpenChange={setIsUnknownLocationDropdownOpen}>
                    <PopoverAnchor asChild>
                      <div className="relative w-full">
                        {addressInput}
                        <button
                          type="button"
                          className="absolute right-2 top-1/2 -translate-y-1/2"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => setIsUnknownLocationDropdownOpen((prev) => !prev)}
                          aria-label="Address options"
                        >
                          <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />
                        </button>
                      </div>
                    </PopoverAnchor>
                    <PopoverContent
                      className="p-1"
                      align="start"
                      onOpenAutoFocus={(e) => e.preventDefault()}
                    >
                      <button
                        type="button"
                        className={cn(
                          "flex w-full items-center gap-2 rounded-sm border-0 bg-transparent px-2 py-2 text-left text-sm shadow-none outline-none hover:bg-accent hover:text-accent-foreground focus:outline-none focus-visible:outline-none focus-visible:ring-0"
                        )}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => selectUnknownLocation(field.onChange)}
                      >
                        <Check className={cn("h-4 w-4", isUnknownLocationValue(String(rawValue ?? "")) ? "opacity-100 text-primary" : "opacity-0")} />
                        <span>{LOCATION_CURRENTLY_UNKNOWN_OPTION}</span>
                      </button>
                    </PopoverContent>
                  </Popover>
                ) : (
                  addressInput
                )}
              </FormTextInputWrapper>
            </Autocomplete>
          )
        }}
      />

      {/* "I don't have a street address" link */}
      <div className="mt-2">
        <Button
          type="button"
          variant="link"
          className="h-auto p-0 text-sm text-blue-600 hover:text-blue-700 hover:underline"
          onClick={openMapPinDrop}
          showLoader={false}
        >
          I don&apos;t have a street address
        </Button>
      </div>

    </div>
  )
}
